import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { getPageTitle } from '@configs/routes';
import { trackPageDuration } from './analyticsService';

interface UsePageDurationOptions {
  userId?: string;
  pageName?: string;
}

/**
 * 페이지 체류 시간을 추적하는 훅
 * 페이지 이탈 시 (visibilitychange, beforeunload) 체류 시간을 전송합니다.
 */
export const usePageDuration = (options: UsePageDurationOptions = {}) => {
  const { pathname } = useLocation();
  const { userId, pageName: customPageName } = options;

  const startTimeRef = useRef<number>(Date.now());
  const maxScrollDepthRef = useRef<number>(0);
  const hasSentRef = useRef<boolean>(false);

  // 스크롤 깊이 계산
  const updateScrollDepth = useCallback(() => {
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    const scrollPercent = docHeight > 0 ? Math.round((scrollTop / docHeight) * 100) : 0;
    maxScrollDepthRef.current = Math.max(maxScrollDepthRef.current, scrollPercent);
  }, []);

  // 체류 시간 전송
  const sendDuration = useCallback(() => {
    if (hasSentRef.current) return;
    hasSentRef.current = true;

    const duration = Date.now() - startTimeRef.current;
    const pageName = customPageName || getPageTitle(pathname) || pathname;

    trackPageDuration(
      {
        pageUrl: pathname,
        pageName,
        duration,
        scrollDepth: maxScrollDepthRef.current,
      },
      userId,
    );
  }, [pathname, userId, customPageName]);

  useEffect(() => {
    // 페이지 진입 시 초기화
    startTimeRef.current = Date.now();
    maxScrollDepthRef.current = 0;
    hasSentRef.current = false;

    // 스크롤 이벤트 리스너
    window.addEventListener('scroll', updateScrollDepth, { passive: true });

    // 페이지 이탈 감지
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        sendDuration();
      }
    };

    const handleBeforeUnload = () => {
      sendDuration();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      // cleanup 시에도 전송 (라우트 변경 시)
      sendDuration();

      window.removeEventListener('scroll', updateScrollDepth);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [pathname, updateScrollDepth, sendDuration]);
};
